import type { ReactNode } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useIsDarkTheme } from '@/stores/ui.store';
import { ChartFrame } from './ChartFrame';
import { ChartTooltip, type ValueFormatter } from './ChartTooltip';
import { chartTheme, seriesColor } from './palette';

export interface LineSeries {
  key: string;
  label: string;
  formatter?: ValueFormatter;
}

export interface LineChartCardProps<T extends Record<string, unknown>> {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  data: T[];
  xKey: string;
  series: LineSeries[];
  height?: number;
  loading?: boolean;
  emptyMessage?: string;
  xTickFormatter?: (value: string) => string;
  yTickFormatter?: (value: number) => string;
  footer?: ReactNode;
  className?: string;
}

/** Série temporal. Um único eixo Y — nunca dois. */
export function LineChartCard<T extends Record<string, unknown>>({
  title,
  description,
  actions,
  data,
  xKey,
  series,
  height = 280,
  loading,
  emptyMessage,
  xTickFormatter,
  yTickFormatter,
  footer,
  className,
}: LineChartCardProps<T>) {
  const isDark = useIsDarkTheme();
  const theme = chartTheme(isDark);
  const formatters = Object.fromEntries(
    series.filter((item) => item.formatter).map((item) => [item.key, item.formatter as ValueFormatter]),
  );

  return (
    <ChartFrame
      title={title}
      description={description}
      actions={actions}
      height={height}
      loading={loading}
      empty={data.length === 0}
      emptyMessage={emptyMessage}
      footer={footer}
      className={className}
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={xKey}
            tickFormatter={xTickFormatter}
            tick={{ fill: theme.text, fontSize: 11 }}
            axisLine={{ stroke: theme.axis }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            tickFormatter={yTickFormatter}
            tick={{ fill: theme.text, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={64}
          />
          <Tooltip
            cursor={{ stroke: theme.axis, strokeWidth: 1 }}
            content={
              <ChartTooltip
                theme={theme}
                formatters={formatters}
                labelFormatter={xTickFormatter}
              />
            }
          />
          {series.length > 1 && (
            <Legend
              iconType="plainline"
              wrapperStyle={{ fontSize: 12, color: theme.text, paddingTop: 8 }}
            />
          )}
          {series.map((item, index) => (
            <Line
              key={item.key}
              type="monotone"
              dataKey={item.key}
              name={item.label}
              stroke={seriesColor(theme, index)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: theme.surface }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
